import React, { useEffect, useMemo, useState } from 'react';
import { X, Calendar, FileText, Loader2, MapPin, Tag, Users, Paperclip, Trash2, ExternalLink, Pencil, Upload, Eye, Info, Plus, Settings } from 'lucide-react';
import Swal from 'sweetalert2';
import { crmUpdateMeeting, crmUploadMinute, crmDeleteMinute, crmDeleteMeeting } from '../../../../services/vinculationCRMService';

import TooltipHelper from '../../../../components/common/TooltipHelper';
import { getCategories, addCategory, deleteCategory, getCustomCategories } from '../../../../services/meetingCategoriesService';

const parseDateToLocal = (dateString) => {
  if (!dateString) return null;
  const cleanDateString = dateString.split('T')[0];
  const parts = cleanDateString.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

const onlyDate = (iso) => {
  if (!iso) return '';
  const d = parseDateToLocal(iso);
  if (!d || Number.isNaN(+d)) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

// Convierte fecha ISO a formato YYYY-MM-DD para inputs de tipo date
const dateToInputFormat = (iso) => {
  if (!iso) return '';
  const d = parseDateToLocal(iso);
  if (!d || Number.isNaN(+d)) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function ChipsInput({ icon, placeholder = 'Escribe y Enter', values, onChange, hint }) {
  const [txt, setTxt] = useState('');
  const add = (raw) => {
    const v = String(raw || '').trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    onChange([...values, v]);
    setTxt('');
  };
  const removeAt = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
        {icon} {placeholder.includes('Enter') ? placeholder.replace('... (Enter)', '') : placeholder}
        {hint && <TooltipHelper text={hint} />}
      </label>
      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-white px-2 py-2 focus-within:ring-2 focus-within:ring-[#0488AF]">
        {values.map((v, i) => (
          <span key={`${v}-${i}`} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border bg-gray-100 text-gray-700">
            {v}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="w-5 h-5 grid place-items-center rounded-full hover:bg-gray-200"
              aria-label="Quitar"
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[160px] border-0 outline-none bg-transparent py-1 px-2 text-sm"
          value={txt}
          onChange={(e) => setTxt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(txt); } }}
          onBlur={() => { if (txt.trim()) add(txt); }}
          placeholder="Escribe el nombre y presiona Enter"
        />
      </div>
    </div>
  );
}

export default function MeetingDetailsModal({
  open,
  rut,
  meeting,
  minutes = [],
  onClose,
  onUpdated,
  onMinuteAdded,
  onMinuteDeleted,
  onDeleted,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileList, setFileList] = useState([]); // Array de {file: File, name: string}

  // form state
  const [date, setDate] = useState('');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('OTRA');
  const [location, setLocation] = useState('');
  const [participants, setParticipants] = useState([]);
  const [tags, setTags] = useState([]);

  // Categorías (iniciales + personalizadas)
  const [categories, setCategories] = useState(getCategories());
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  // Cargar categorías al montar y cuando cambien en localStorage
  useEffect(() => {
    const loadCategories = () => {
      setCategories(getCategories());
    };
    
    loadCategories();
    
    // Escuchar cambios en localStorage (para sincronizar entre pestañas/componentes)
    const handleStorageChange = (e) => {
      if (e.key === 'crm_meeting_categories') {
        loadCategories();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // También escuchar eventos personalizados para cambios en la misma pestaña
    window.addEventListener('categoriesUpdated', loadCategories);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('categoriesUpdated', loadCategories);
    };
  }, []);

  const handleAddCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      Swal.fire('Campo vacío', 'Ingresa un nombre para la categoría', 'info');
      return;
    }
    
    try {
      addCategory(trimmed);
      const upperValue = trimmed.toUpperCase().replace(/\s+/g, '_');
      setCategories(getCategories());
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      setCategory(upperValue); // Seleccionar la nueva categoría automáticamente
      
      // Disparar evento para actualizar otros componentes
      window.dispatchEvent(new Event('categoriesUpdated'));
      
      Swal.fire('Categoría agregada', 'La categoría se ha agregado correctamente', 'success');
      // Cerrar el modal después de agregar
      setShowCategoriesModal(false);
    } catch (err) {
      Swal.fire('Error', err.message || 'No se pudo agregar la categoría', 'error');
    }
  };

  const handleDeleteCategory = async (categoryValue) => {
    const cat = categories.find(c => c.value === categoryValue);
    const categoryName = cat?.label || categoryValue;
    
    // Verificar si es la última categoría
    const allCategories = getCategories();
    if (allCategories.length <= 1) {
      Swal.fire({
        icon: 'warning',
        title: 'No se puede eliminar',
        html: 'No se puede eliminar la última categoría. Crea una nueva categoría antes de eliminar esta.<br/><br/><strong>Nota:</strong> Las reuniones que usan esta categoría quedarán sin categoría asignada.',
        confirmButtonColor: '#0488AF',
      });
      return;
    }
    
    const result = await Swal.fire({
      title: '¿Eliminar categoría?',
      html: `¿Estás seguro de eliminar la categoría "${categoryName}"?<br/><br/><strong>Nota:</strong> Las reuniones que usan esta categoría quedarán sin categoría asignada. Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        // Si la categoría a eliminar está seleccionada, cambiarla a la primera disponible
        if (category === categoryValue) {
          const remainingCategories = getCategories().filter(c => c.value !== categoryValue);
          if (remainingCategories.length > 0) {
            setCategory(remainingCategories[0].value);
          }
        }
        
        deleteCategory(categoryValue);
        setCategories(getCategories());
        
        // Disparar evento para actualizar otros componentes
        window.dispatchEvent(new Event('categoriesUpdated'));
        
        Swal.fire('Eliminada', `La categoría "${categoryName}" ha sido eliminada. Las reuniones que la usaban ahora están sin categoría asignada.`, 'success');
        // Cerrar el modal después de eliminar
        setShowCategoriesModal(false);
      } catch (err) {
        Swal.fire('Error', err.message || 'No se pudo eliminar la categoría', 'error');
      }
    }
  };

  useEffect(() => {
    if (!open || !meeting) return;
    setIsEditing(false);
    setDate(dateToInputFormat(meeting.date));
    setTitle(meeting.title || '');
    setDesc(meeting.description || '');
    setCategory(String(meeting.category || 'OTRA'));
    setLocation(meeting.location || '');
    setParticipants(Array.isArray(meeting.participants) ? meeting.participants : []);
    setTags(Array.isArray(meeting.tags) ? meeting.tags : []);
    setFileList([]);
  }, [open, meeting]);

  const canSave = useMemo(() => !!date, [date]);

  const handleSave = async () => {
    if (!meeting?.id || !canSave) return;
    try {
      setSaving(true);
      
      // Guardar cambios de la reunión
      const payload = {
        date,
        title: title.trim() || null,
        description: desc.trim() || null,
        category: category || null,
        location: location.trim() || null,
        participants: participants.length > 0 ? participants : null,
        tags: tags.length > 0 ? tags : null,
      };
      const updated = await crmUpdateMeeting(meeting.id, payload);
      const item = updated?.data || updated;
      onUpdated?.(item);
      // Disparar evento global para actualizar reuniones recientes
      window.dispatchEvent(new Event('meetingUpdated'));

      // Subir archivos si hay archivos seleccionados
      const filesToUpload = [...fileList];
      if (filesToUpload.length > 0) {
        let uploadSwal = null;
        if (filesToUpload.length > 0) {
          uploadSwal = Swal.fire({
            title: 'Subiendo minutas...',
            html: `Subiendo 1 de ${filesToUpload.length}`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });
        }
        
        for (let i = 0; i < filesToUpload.length; i++) {
          const item = filesToUpload[i];
          const fileName = item.file.name; // Usar siempre el nombre original del archivo
          try {
            if (uploadSwal && filesToUpload.length > 1) {
              Swal.update({
                html: `Subiendo ${i + 1} de ${filesToUpload.length}<br/><small>${fileName}</small>`
              });
            }
            const up = await crmUploadMinute({
              rut,
              title: fileName,
              date,
              file: item.file,
              meetingId: meeting.id, // asocia a la reunión
            });
            onMinuteAdded?.(up?.data || up);
          } catch (uploadError) {
            if (uploadSwal) {
              Swal.close();
            }
            throw new Error(`Error al subir "${fileName}": ${uploadError?.message || 'Error desconocido'}`);
          }
        }
        
        if (uploadSwal) {
          Swal.close();
        }
        setFileList([]);
      }

      setIsEditing(false);
      const messages = [];
      messages.push('Reunión actualizada');
      if (filesToUpload.length > 0) {
        messages.push(`${filesToUpload.length} minuta(s) subida(s)`);
      }
      Swal.fire('Guardado', messages.join('. '), 'success');
    } catch (e) {
      Swal.fire('Error', e?.message || 'No se pudo guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !meeting) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-start justify-center p-4 overflow-auto">
        <div className="w-full max-w-4xl my-4 rounded-2xl bg-white border shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white flex items-center justify-between flex-none">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-white/10 border border-white/20">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-semibold truncate">{meeting.title || 'Reunión'}</div>
              </div>
            </div>
                <div className="flex items-center gap-2 flex-shrink-0">
              {!isEditing && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] whitespace-nowrap transition-colors"
                    title="Editar reunión"
                  >
                    <Pencil className="w-4 h-4" /> Editar
                  </button>
                  <button
                    onClick={async () => {
                      const meetingName = meeting.title || 'esta reunión';
                      const ask = await Swal.fire({ 
                        title: 'Eliminar reunión', 
                        text: `¿Estás seguro de eliminar "${meetingName}"? Esta acción no se puede deshacer.`,
                        icon: 'warning', 
                        showCancelButton: true,
                        confirmButtonColor: '#d33',
                        cancelButtonColor: '#3085d6',
                        confirmButtonText: 'Sí, eliminar',
                        cancelButtonText: 'Cancelar'
                      });
                      if (!ask.isConfirmed) return;
                      try {
                        await crmDeleteMeeting(meeting.id, rut);
                        onDeleted?.(meeting.id);
                        // Disparar evento global para actualizar reuniones recientes
                        window.dispatchEvent(new Event('meetingDeleted'));
                        onClose();
                        Swal.fire('Eliminada', `La reunión "${meetingName}" ha sido eliminada`, 'success');
                      } catch (err) {
                        Swal.fire('Error', err?.message || 'No se pudo eliminar la reunión', 'error');
                      }
                    }}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold whitespace-nowrap transition-colors"
                    title="Eliminar reunión"
                  >
                    <Trash2 className="w-4 h-4" /> Eliminar
                  </button>
                </>
              )}
              <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0" title="Cerrar">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Vista */}
            {!isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50 to-white p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-[#0488AF]" />
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha</div>
                    </div>
                    <div className="text-lg font-semibold text-gray-800">{onlyDate(meeting.date)}</div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-purple-50 to-white p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-purple-600" />
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Categoría</div>
                    </div>
                    <div className="text-lg font-semibold text-gray-800">
                      {getCategories().find(c => c.value === meeting.category)?.label || String(meeting.category || 'OTRA')}
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-[#0488AF]" />
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Título</div>
                  </div>
                  <div className="text-base font-semibold text-gray-800">{meeting.title || '—'}</div>
                </div>
                {meeting.description && (
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-[#0488AF]" />
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Descripción</div>
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{meeting.description}</div>
                  </div>
                )}
                {meeting.location && (
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-[#0488AF]" />
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Lugar</div>
                    </div>
                    <div className="text-sm text-gray-700">{meeting.location}</div>
                  </div>
                )}
                {meeting.participants && Array.isArray(meeting.participants) && meeting.participants.length > 0 && (
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-[#0488AF]" />
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Participantes</div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {meeting.participants.map((p, i) => (
                        <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {meeting.tags && Array.isArray(meeting.tags) && meeting.tags.length > 0 && (
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-[#0488AF]" />
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Etiquetas</div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {meeting.tags.map((t, i) => (
                        <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Fecha de la reunión <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Título <span className="text-gray-400 text-xs">(opcional)</span>
                    </label>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Ej: Reunión de coordinación, Presentación de proyecto..."
                      title="Título breve que identifique la reunión"
                      className="w-full h-11 px-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Descripción / Acuerdos <span className="text-gray-400 text-xs">(opcional)</span>
                  </label>
                  <textarea
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder="Describe los temas tratados, acuerdos alcanzados, próximos pasos, etc."
                    title="Información detallada sobre la reunión: temas tratados, acuerdos, compromisos, etc."
                    rows={3}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Categoría
                    </label>
                    <div className="relative flex items-center gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                        >
                          {categories.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowCategoriesModal(true)}
                        className="h-11 px-3 flex items-center gap-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 hover:text-[#0488AF] transition-colors text-sm font-medium whitespace-nowrap"
                        title="Gestionar categorías"
                      >
                        <Settings className="w-4 h-4" />
                        <span className="hidden sm:inline">Gestionar</span>
                      </button>
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-lg border-2 border-blue-300 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 shadow-sm mt-1.5">
                      <span className="text-sm leading-none">ℹ️</span>
                      <span>
                        Si no encuentras la categoría que buscas, créala en{" "}
                        <span className="font-bold text-blue-900">"Gestionar"</span>.
                      </span>
                    </div>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Lugar <span className="text-gray-400 text-xs">(opcional)</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="Ej: Oficina principal, Sala de reuniones, Virtual (Zoom)..."
                        title="Dirección física o plataforma virtual donde se realizó la reunión"
                        className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                      />
                    </div>
                  </div>
                </div>


                {/* PARTICIPANTES / TAGS de REUNIÓN (chips con Enter) */}
                <div className="space-y-4">
                  <div>
                    <ChipsInput
                      icon={<Users className="w-4 h-4" />}
                      placeholder="Participantes de la reunión"
                      values={participants}
                      onChange={setParticipants}
                      hint="Escribe el nombre de cada participante y presiona Enter para agregarlo. Estos participantes se asociarán a la reunión."
                    />
                  </div>
                  <div>
                    <ChipsInput
                      icon={<Tag className="w-4 h-4" />}
                      placeholder="Etiquetas de la reunión… (Enter)"
                      values={tags}
                      onChange={setTags}
                      hint='Escribe etiquetas para categorizar la reunión (ej: "innovación", "convenio"). Presiona Enter para agregar cada etiqueta.'
                    />
                  </div>
                </div>

                {/* Minutas existentes */}
                {minutes.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-base font-semibold text-[#0b3a66] mb-3">Minutas existentes</h4>
                    <ul className="space-y-2">
                      {minutes.map((d) => {
                        const url = d.viewUrl || d.downloadUrl || d.fileSecureUrl || d.fileUrl;
                        return (
                          <li key={d.id} className="p-3 rounded-xl border bg-white flex items-center justify-between gap-2">
                            <div className="min-w-0 flex items-center gap-3">
                              <Paperclip className="w-4 h-4 text-gray-500 shrink-0" />
                              <a
                                href={url || '#'}
                                target={url ? '_blank' : undefined}
                                rel={url ? 'noopener noreferrer' : undefined}
                                className={`truncate ${url ? 'text-[#0B4A8F] hover:underline font-semibold' : 'text-gray-700'}`}
                                onClick={(e) => !url && e.preventDefault()}
                                title={d.title}
                              >
                                {d.title || d.originalName || 'archivo'}
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={url || '#'}
                                target={url ? '_blank' : undefined}
                                rel={url ? 'noopener noreferrer' : undefined}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-gray-50 text-gray-700"
                                title={url ? 'Ver' : 'Sin URL'}
                                onClick={(e) => !url && e.preventDefault()}
                              >
                                <Eye className="w-4 h-4" />
                              </a>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Subir nuevas minutas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                    <span className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Minutas <span className="text-gray-400 text-xs"></span>
                    </span>
                    <TooltipHelper text="Puedes seleccionar distintos formatos de archivos. Formatos aceptados: PDF, documentos (DOC, DOCX, XLS, XLSX), presentaciones (PPT, PPTX), imágenes (PNG, JPG, WEBP) y CSV." />
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      
                      // Validar tamaño de PDFs
                      for (const file of files) {
                        if (file.type === 'application/pdf' && file.size > 15 * 1024 * 1024) {
                          await Swal.fire({
                            icon: 'warning',
                            title: 'Archivo muy grande',
                            html: `El archivo "${file.name}" es demasiado grande (máx 15 MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
                            confirmButtonText: 'Entendido',
                          });
                          e.target.value = '';
                          return;
                        }
                      }
                      
                      setFileList(files.map(f => ({ file: f, name: f.name }))); // name solo para visualización
                    }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.csv"
                    className="mt-2 block w-full h-11 px-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#0488AF]/10 file:text-[#0488AF] hover:file:bg-[#0488AF]/20 cursor-pointer"
                  />
                  {fileList.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {fileList.map((item, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700"
                        >
                          {item.name}
                          <button
                            type="button"
                            onClick={() => setFileList(fileList.filter((_, idx) => idx !== i))}
                            className="ml-1 text-gray-500 hover:text-red-600"
                            aria-label="Quitar archivo"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Botones de acción */}
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="h-11 px-6 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !canSave}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 text-sm font-semibold transition-colors"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Guardar
                  </button>
                </div>
              </form>
            )}

            {/* Minutas - Solo en modo visualización */}
            {!isEditing && (
              <div className="pt-6 border-t-2 border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-[#0b3a66]">Minutas</h4>
                </div>
                {minutes.length === 0 ? (
                  <div className="rounded-xl border border-dashed p-4 text-gray-600 bg-gray-50">
                    No hay minutas asociadas a esta reunión.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {minutes.map((d) => {
                      const url = d.viewUrl || d.downloadUrl || d.fileSecureUrl || d.fileUrl;
                      return (
                        <li key={d.id} className="p-3 rounded-xl border bg-white flex items-center justify-between gap-2">
                          <div className="min-w-0 flex items-center gap-3">
                            <Paperclip className="w-4 h-4 text-gray-500 shrink-0" />
                            <a
                              href={url || '#'}
                              target={url ? '_blank' : undefined}
                              rel={url ? 'noopener noreferrer' : undefined}
                              className={`truncate ${url ? 'text-[#0B4A8F] hover:underline font-semibold' : 'text-gray-700'}`}
                              onClick={(e) => !url && e.preventDefault()}
                              title={d.title}
                            >
                              {d.title || d.originalName || 'archivo'}
                            </a>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={url || '#'}
                              target={url ? '_blank' : undefined}
                              rel={url ? 'noopener noreferrer' : undefined}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-gray-50 text-gray-700"
                              title={url ? 'Ver' : 'Sin URL'}
                              onClick={(e) => !url && e.preventDefault()}
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>{/* body */}
        </div>
      </div>

      {/* Modal de gestión de categorías */}
      {showCategoriesModal && (
        <div className="fixed inset-0 z-[90]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCategoriesModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-2xl bg-white border shadow-xl overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">Gestionar categorías</h3>
                </div>
                <button
                  onClick={() => setShowCategoriesModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Lista de categorías */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categorías existentes</label>
                  {categories.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {categories.map((c) => (
                        <div
                          key={c.value}
                          className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-700">{c.label}</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteCategory(c.value)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Eliminar categoría"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic py-4 text-center">No hay categorías. Crea una nueva abajo.</p>
                  )}
                </div>

                {/* Agregar nueva categoría */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Agregar nueva categoría</label>
                  {!showNewCategoryInput ? (
                    <button
                      type="button"
                      onClick={() => setShowNewCategoryInput(true)}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] px-4 py-2 text-sm font-semibold text-white shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors"
                    >
                      <Plus size={16} />
                      Agregar categoría
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCategory();
                          }
                          if (e.key === 'Escape') {
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          }
                        }}
                        placeholder="Escribe el nombre y presiona Enter"
                        className="w-full h-10 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddCategory}
                          className="flex-1 h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold transition-colors inline-flex items-center justify-center gap-1.5"
                        >
                          <Plus className="w-4 h-4" />
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          }}
                          className="h-10 px-4 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
